import type { SessionContext } from '@pedigreesmart/core'

/**
 * Typed HTTP client for the PedigreeSmart API.
 *
 * No DOM, no React: this package is imported unchanged by a future React
 * Native app. Token storage is injected rather than assumed, because that is
 * the one thing that genuinely differs between web (memory + httpOnly cookie)
 * and native (secure storage).
 */

export interface TokenStore {
  getAccessToken(): string | null
  getRefreshToken(): string | null
  setTokens(accessToken: string, refreshToken: string): void
  clear(): void
}

export interface ApiProblem {
  type: string
  title: string
  status: number
  detail?: string
  violations?: ReadonlyArray<{ propertyPath: string; message: string; code?: string }>
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: ApiProblem,
  ) {
    super(problem.detail ?? problem.title)
    this.name = 'ApiError'
  }

  get violations(): ReadonlyArray<{ propertyPath: string; message: string }> {
    return this.problem.violations ?? []
  }

  /** Violations keyed by field, ready to hand to a form library. */
  fieldErrors(): Record<string, string> {
    const errors: Record<string, string> = {}

    for (const violation of this.violations) {
      errors[violation.propertyPath] ??= violation.message
    }

    return errors
  }
}

export interface ClientOptions {
  baseUrl: string
  tokens: TokenStore
  /** Called when refreshing fails, i.e. the session is genuinely over. */
  onSessionExpired?: () => void
  fetch?: typeof globalThis.fetch
}

interface RequestOptions {
  method?: string
  body?: unknown
  query?: Record<string, string | number | boolean | undefined>
  signal?: AbortSignal
  /** Set on POSTs with side effects: declarations, orders. */
  idempotencyKey?: string
}

export class ApiClient {
  private refreshing: Promise<boolean> | null = null

  constructor(private readonly options: ClientOptions) {}

  private get fetchImpl(): typeof globalThis.fetch {
    return this.options.fetch ?? globalThis.fetch
  }

  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const response = await this.send(path, options)

    // One retry after a refresh. Not a loop: if the refreshed token is also
    // rejected, the session is over and retrying would spin.
    if (response.status === 401 && (await this.refresh())) {
      const retried = await this.send(path, options)

      return this.unwrap<T>(retried)
    }

    return this.unwrap<T>(response)
  }

  get<T>(path: string, query?: RequestOptions['query'], signal?: AbortSignal): Promise<T> {
    return this.request<T>(path, { method: 'GET', ...(query ? { query } : {}), ...(signal ? { signal } : {}) })
  }

  post<T>(path: string, body?: unknown, idempotencyKey?: string): Promise<T> {
    return this.request<T>(path, {
      method: 'POST',
      body,
      ...(idempotencyKey ? { idempotencyKey } : {}),
    })
  }

  patch<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(path, { method: 'PATCH', body })
  }

  /**
   * `PUT` e non `PATCH`: le rotte che lo usano sostituiscono uno stato intero
   * — la genealogia, l'interruttore della vetrina — e non ne ritoccano un
   * pezzo. Manda `application/json`, non il merge-patch.
   */
  put<T>(path: string, body: unknown): Promise<T> {
    return this.request<T>(path, { method: 'PUT', body })
  }

  delete<T>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'DELETE' })
  }

  /**
   * Scarica un file, con il nome che gli dà il server.
   *
   * Un semplice collegamento non basterebbe: queste risorse vogliono il
   * token, e un `<a href>` non lo porta. Il file arriva quindi come richiesta
   * autenticata e il browser lo salva da lì.
   *
   * Il nome viene da `Content-Disposition`, non inventato dal client: il
   * server lo costruisce con il numero di registro
   * (`certificato-ROI18-00002.pdf`), che è il modo in cui quel documento
   * verrà cercato dentro una cartella fra due anni.
   */
  async file(path: string, signal?: AbortSignal): Promise<{ blob: Blob; filename: string }> {
    const opzioni: RequestOptions = { method: 'GET', ...(signal ? { signal } : {}) }
    let response = await this.send(path, opzioni)

    if (response.status === 401 && (await this.refresh())) {
      response = await this.send(path, opzioni)
    }

    if (!response.ok) {
      const testo = await response.text()
      const problema: unknown = testo ? JSON.parse(testo) : null

      throw new ApiError(
        response.status,
        problema && typeof problema === 'object'
          ? (problema as ApiProblem)
          : { type: 'about:blank', title: response.statusText, status: response.status },
      )
    }

    return {
      blob: await response.blob(),
      filename: nomeDelFile(response.headers.get('Content-Disposition')),
    }
  }

  /**
   * Manda un `multipart/form-data`.
   *
   * **Non** si imposta il `Content-Type`: lo compone il browser, e deve
   * contenere il `boundary` che separa le parti. Scriverlo a mano produce un
   * corpo che il server non sa dividere, e l'errore che ne esce parla di campi
   * mancanti invece che di intestazione sbagliata.
   */
  async form<T>(path: string, body: FormData): Promise<T> {
    const invia = () =>
      this.fetchImpl(new URL(`${this.options.baseUrl}${path}`), {
        method: 'POST',
        headers: this.authOnly(),
        body,
      })

    let response = await invia()

    if (response.status === 401 && (await this.refresh())) {
      response = await invia()
    }

    return this.unwrap<T>(response)
  }

  private authOnly(): Record<string, string> {
    const headers: Record<string, string> = {
      Accept: 'application/ld+json, application/json;q=0.9',
    }
    const token = this.options.tokens.getAccessToken()

    if (token) headers.Authorization = `Bearer ${token}`

    return headers
  }

  // ----------------------------------------------------------------- auth

  async login(email: string, password: string): Promise<SessionContext> {
    const result = await this.request<{
      token: string
      refresh_token: string
      context: SessionContext
    }>('/auth/login', { method: 'POST', body: { email, password } })

    this.options.tokens.setTokens(result.token, result.refresh_token)

    return result.context
  }

  /**
   * Re-issues the token for another association. The tenant lives in the
   * token's `tid` claim, never in a header the client could tamper with, so
   * switching means getting a new token.
   */
  async switchTenant(tenantId: number): Promise<SessionContext> {
    const result = await this.request<{
      token: string
      refresh_token: string
      context: SessionContext
    }>('/auth/switch-tenant', { method: 'POST', body: { tenant_id: tenantId } })

    this.options.tokens.setTokens(result.token, result.refresh_token)

    return result.context
  }

  me(): Promise<SessionContext> {
    return this.get<SessionContext>('/me')
  }

  async logout(): Promise<void> {
    const refreshToken = this.options.tokens.getRefreshToken()

    if (refreshToken) {
      await this.request('/auth/logout', { method: 'POST', body: { refresh_token: refreshToken } }).catch(() => {
        // A failed logout must not strand the user in a logged-in UI.
      })
    }

    this.options.tokens.clear()
  }

  // -------------------------------------------------------------- internals

  private async send(path: string, options: RequestOptions): Promise<Response> {
    const url = new URL(`${this.options.baseUrl}${path}`)

    for (const [key, value] of Object.entries(options.query ?? {})) {
      if (value !== undefined) url.searchParams.set(key, String(value))
    }

    // ld+json e non json: è il formato che porta con sé `member` e
    // `totalItems`, cioè la paginazione su cui è tipizzato Page<T>. Gli
    // endpoint di autenticazione, che non passano da API Platform,
    // rispondono comunque JSON semplice.
    const headers: Record<string, string> = {
      Accept: 'application/ld+json, application/json;q=0.9',
    }
    const token = this.options.tokens.getAccessToken()

    if (token) headers.Authorization = `Bearer ${token}`
    // Su PATCH il tipo **non** è `application/json`: API Platform accetta solo
    // `merge-patch+json` e risponde `415` a tutto il resto. Non è una
    // pignoleria formale — è la differenza fra «sostituisci la risorsa» e
    // «cambia solo i campi che ti mando», e il modulo di modifica manda solo
    // quelli. Fino al 9 agosto qui c'era `application/json` per tutti i
    // metodi: ogni salvataggio avrebbe risposto `415`.
    if (options.body !== undefined) {
      headers['Content-Type'] =
        options.method === 'PATCH' ? 'application/merge-patch+json' : 'application/json'
    }
    if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey

    return this.fetchImpl(url, {
      method: options.method ?? 'GET',
      headers,
      ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
      ...(options.signal ? { signal: options.signal } : {}),
    })
  }

  /** Concurrent 401s share one refresh instead of racing several. */
  private refresh(): Promise<boolean> {
    this.refreshing ??= this.doRefresh().finally(() => {
      this.refreshing = null
    })

    return this.refreshing
  }

  private async doRefresh(): Promise<boolean> {
    const refreshToken = this.options.tokens.getRefreshToken()

    if (!refreshToken) {
      this.options.onSessionExpired?.()

      return false
    }

    const response = await this.send('/auth/refresh', {
      method: 'POST',
      body: { refresh_token: refreshToken },
    })

    if (!response.ok) {
      this.options.tokens.clear()
      this.options.onSessionExpired?.()

      return false
    }

    const result = (await response.json()) as { token: string; refresh_token: string }
    this.options.tokens.setTokens(result.token, result.refresh_token)

    return true
  }

  private async unwrap<T>(response: Response): Promise<T> {
    if (response.status === 204) return undefined as T

    const text = await response.text()
    const payload: unknown = text ? JSON.parse(text) : null

    if (response.ok) return payload as T

    const problem: ApiProblem =
      payload && typeof payload === 'object'
        ? (payload as ApiProblem)
        : { type: 'about:blank', title: response.statusText, status: response.status }

    throw new ApiError(response.status, problem)
  }
}

/**
 * Il nome del file dentro `Content-Disposition`.
 *
 * Si accontenta della forma che manda il nostro server —
 * `attachment; filename="certificato-ROI18-00002.pdf"` — e ricade su un nome
 * generico se manca o è in una forma diversa: meglio un file salvato con un
 * nome brutto che un download che non parte.
 */
function nomeDelFile(intestazione: string | null): string {
  const trovato = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(intestazione ?? '')

  return trovato?.[1] ? decodeURIComponent(trovato[1]) : 'documento.pdf'
}
